// Hook for auto-logout after inactivity
// Works even when browser tab is inactive or computer sleeps
import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook to detect user inactivity and trigger a callback after a specified timeout
 * @param {Function} onTimeout - Callback function to execute when timeout is reached
 * @param {number} timeoutMs - Timeout duration in milliseconds (default: 1 hour)
 * @param {boolean} enabled - Whether the timeout is enabled (default: true)
 */
const useInactivityTimeout = (onTimeout, timeoutMs = 60 * 60 * 1000, enabled = true) => {
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const onTimeoutRef = useRef(onTimeout);
  const hasTimedOutRef = useRef(false);

  // Keep onTimeout ref updated
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  // Check if timeout has been exceeded (works even after sleep/background)
  const checkTimeout = useCallback(() => {
    if (hasTimedOutRef.current) return true; // Already timed out
    
    const elapsed = Date.now() - lastActivityRef.current;
    if (elapsed >= timeoutMs) {
      console.log(`[Inactivity] Session expired after ${Math.round(elapsed / 1000 / 60)} minutes of inactivity`);
      hasTimedOutRef.current = true;
      onTimeoutRef.current();
      return true;
    }
    return false;
  }, [timeoutMs]);

  // Reset the timer
  const resetTimer = useCallback(() => {
    if (hasTimedOutRef.current) return; // Don't reset if already timed out
    
    lastActivityRef.current = Date.now();
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    if (enabled) {
      // Use shorter interval checks to handle browser throttling
      const checkInterval = Math.min(timeoutMs, 30000); // Check every 30 seconds max
      timeoutRef.current = setTimeout(() => {
        if (!checkTimeout()) {
          resetTimer(); // Schedule another check if not timed out
        }
      }, checkInterval);
    }
  }, [timeoutMs, enabled, checkTimeout]);

  // Handle user activity - debounced
  const handleActivity = useCallback(() => {
    if (hasTimedOutRef.current) return;
    
    const now = Date.now();
    if (now - lastActivityRef.current > 1000) {
      lastActivityRef.current = now;
      resetTimer();
    }
  }, [resetTimer]);

  // Handle visibility change (user returns to tab after being away)
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible' && !hasTimedOutRef.current) {
      console.log('[Inactivity] Tab visible, checking timeout...');
      if (!checkTimeout()) {
        resetTimer();
      }
    }
  }, [checkTimeout, resetTimer]);

  // Handle window focus
  const handleFocus = useCallback(() => {
    if (!hasTimedOutRef.current) {
      console.log('[Inactivity] Window focused, checking timeout...');
      if (!checkTimeout()) {
        resetTimer();
      }
    }
  }, [checkTimeout, resetTimer]);

  useEffect(() => {
    if (!enabled) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    // Reset the timed out flag when enabled changes
    hasTimedOutRef.current = false;
    lastActivityRef.current = Date.now();

    console.log(`[Inactivity] Timer initialized: ${timeoutMs / 1000 / 60} minutes`);

    // Activity events
    const activityEvents = [
      'mousedown',
      'mousemove', 
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'wheel'
    ];

    // Add activity listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Add visibility change listener (CRITICAL for sleep/background detection)
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Add focus listener
    window.addEventListener('focus', handleFocus);

    // Start the initial timer
    resetTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [handleActivity, handleVisibilityChange, handleFocus, resetTimer, enabled, timeoutMs]);

  // Return utilities
  const getTimeRemaining = useCallback(() => {
    const elapsed = Date.now() - lastActivityRef.current;
    return Math.max(0, timeoutMs - elapsed);
  }, [timeoutMs]);

  return { resetTimer, getTimeRemaining };
};

export default useInactivityTimeout;
